import type { Team } from "@/types/team.types";
import type { UserResponseSchema } from "@/types/user.types";

export type LoginApiResponse = {
  user: UserApiResponse;
  tokens: {
    accessToken: string;
    idToken: string;
    refreshToken: string;
  };
  features?: string[];
  challengeName?: string;
  session?: string;
  loginSessionToken?: string;
};

export type UserApiResponse = {
  firstName: string;
  lastName: string;
  email: string;
  roleId: string;
  userId: string;
  code: string;
  status: string;
  activatedAt: string;
  deactivatedAt: string | null;
  invitedAt: string;
  invitedById: string;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  roles: UserRoleApiResponse;
  secondaryEmail: string | null;
  phone: string | null;
  primaryAddress: string | null;
  secondaryAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  isOnboarded: boolean;
  teams:Team[]
};

export type UserRoleApiResponse = {
  roleId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  name: string;
  createdById: string | null;
};

export type LoginResponse = {
  user: User;
  tokens: {
    accessToken: string;
    idToken: string;
    refreshToken: string;
  };
  features?: string[];
  challengeName?: string;
  session?: string;
  loginSessionToken?: string;
};

export type User = {
  firstName: string;
  lastName: string;
  email: string;
  roleId: string;
  userId: string;
  code: string;
  status: string;
  activatedAt: string;
  deactivatedAt: string | null;
  invitedAt: string;
  invitedById: string;
  cancelledAt: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  roles: Role;
  secondaryEmail: string | null;
  phone: string | null;
  primaryAddress: string | null;
  secondaryAddress: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  isOnboarded: boolean;
  teams: Team[] | null | undefined;
};

export type Role = {
  roleId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  name: string;
  createdById: string | null;
};

export function mapLoginResponse(apiResponse: LoginApiResponse): LoginResponse {
  return {
    ...apiResponse,
    user: mapUserResponse(apiResponse.user),
    tokens: {
      accessToken: apiResponse.tokens.accessToken,
      idToken: apiResponse.tokens.idToken,
      refreshToken: apiResponse.tokens.refreshToken,
    },
    features: apiResponse.features,
    challengeName: apiResponse.challengeName,
    session: apiResponse.session,
    loginSessionToken: apiResponse.loginSessionToken,
  }
}

export function mapUserResponse(apiUser: UserApiResponse): User {
  return {
    firstName: apiUser.firstName,
    lastName: apiUser.lastName,
    email: apiUser.email,
    roleId: apiUser.roleId,
    userId: apiUser.userId,
    code: apiUser.code,
    status: apiUser.status,
    activatedAt: apiUser.activatedAt,
    deactivatedAt: apiUser.deactivatedAt,
    invitedAt: apiUser.invitedAt,
    invitedById: apiUser.invitedById,
    cancelledAt: apiUser.cancelledAt,
    createdAt: apiUser.createdAt,
    updatedAt: apiUser.updatedAt,
    deletedAt: apiUser.deletedAt,
    roles: mapRoleResponse(apiUser.roles),
    secondaryEmail: apiUser.secondaryEmail,
    phone: apiUser.phone,
    primaryAddress: apiUser.primaryAddress,
    secondaryAddress: apiUser.secondaryAddress,
    city: apiUser.city,
    state: apiUser.state,
    zipCode: apiUser.zipCode,
    isOnboarded: apiUser.isOnboarded,
    teams: apiUser.teams,
  };
}

export function mapRoleResponse(apiRole: UserRoleApiResponse): Role {
  return {
    roleId: apiRole.roleId,
    createdAt: apiRole.createdAt,
    updatedAt: apiRole.updatedAt,
    deletedAt: apiRole.deletedAt,
    name: apiRole.name,
    createdById: apiRole.createdById,
  };
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface RefreshTokenResponse {
  tokens: {
    accessToken: string;
    idToken: string;
  };
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface PasswordChallengeResponse {
  challengeName?: string;
  challengeParameters: {
    requiredAttributes?: string;
    USER_ID_FOR_SRP: string;
    userAttributes?: string;
  };
  session?: string;
  user?: UserResponseSchema;
}

export interface LoginSuccessResponse {
  features: string[];
  tokens: {
    accessToken: string;
    idToken: string;
    refreshToken: string;
  };
  user: UserResponseSchema;
}

export type AuthLoginPostResponse = LoginSuccessResponse | PasswordChallengeResponse;

/** Body for both POST /auth/onboard and POST /auth/confirm-forgot-password. */
export interface AuthChangePasswordRequest {
  newPassword: string;
  payload: string;
}

export interface UserChangePasswordRequest {
  newPassword: string;
  oldPassword: string;
}

export interface MessageResponse {
  message: string;
}

// Log-in history (DGR-1835). Mirrors `LoginClientInfo` in openapi.json.
export type BrandVersion = { brand: string; version: string };

export type ClientInfo = {
  userAgent?: string;
  platform?: string;
  vendor?: string;
  language?: string;
  languages?: string[];
  timezone?: string;
  cookieEnabled?: boolean;
  userAgentData?: {
    brands?: BrandVersion[];
    mobile?: boolean;
    platform?: string;
    platformVersion?: string;
    architecture?: string;
    model?: string;
    fullVersionList?: BrandVersion[];
  };
  screen?: {
    width?: number;
    height?: number;
    availWidth?: number;
    availHeight?: number;
    colorDepth?: number;
    pixelDepth?: number;
    orientation?: string;
  };
  viewport?: { width?: number; height?: number };
  devicePixelRatio?: number;
  maxTouchPoints?: number;
  hardwareConcurrency?: number;
  deviceMemory?: number;
  connection?: { effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean };
};

export type LogoutReason = 'MANUAL' | 'INACTIVITY' | 'BROWSER_CLOSED' | 'SESSION_EXPIRED';
